import CloseIcon from "@mui/icons-material/Close";
import { useCallback, useEffect, useMemo, useState } from "react";
import CountryDropdown from "@/components/giftExperience/CountryDropdown";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { FullscreenPreviewContext } from "@/context/FullscreenPreviewContext";
import type { FullscreenPreviewContextValue } from "@/interfaces/CampaignBuilder";

interface Props {
  onExit: () => void;
  children: React.ReactNode;
  /** When true, children fill the overlay with no padding or centering (e.g. unboxing video) */
  fillContent?: boolean;
}

interface ExitControlProps {
  onExit: () => void;
  condensed?: boolean;
  selectedCountry: string;
  onCountryChange: (code: string) => void;
  className?: string;
}

// Shared markup for the floating pill and the header-hosted copy.
function ExitControl({
  onExit,
  condensed,
  selectedCountry,
  onCountryChange,
  className,
}: ExitControlProps) {
  return (
    <div
      className={`inline-flex items-stretch font-lato${className ? ` ${className}` : ""}`}
      style={{ border: "1.5px solid #333", borderRadius: 6, background: "transparent" }}
    >
      {/* Exit half */}
      <button
        type="button"
        aria-label="Exit preview"
        onClick={onExit}
        className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold text-rodeo-black bg-transparent border-none cursor-pointer transition-colors hover:bg-rodeo-black hover:text-white"
        style={{ borderRadius: "4px 0 0 4px" }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
        {condensed ? "Exit" : "Exit Preview"}
      </button>

      {/* Internal divider */}
      <div aria-hidden="true" style={{ width: 1.5, background: "#333", flexShrink: 0 }} />

      {/* Country picker half */}
      <CountryDropdown compact selectedCountry={selectedCountry} onChange={onCountryChange} />
    </div>
  );
}

export default function FullscreenPreviewOverlay({ onExit, children, fillContent = false }: Props) {
  const [contentVisible, setContentVisible] = useState(false);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [exitInHeader, setExitInHeader] = useState(false);
  const { selectedCountry, setSelectedCountry, setFullscreenPreviewOpen } = useCampaignBuilder();

  // Trigger content fade-in on the next paint so the transition is visible
  useEffect(() => {
    const raf = requestAnimationFrame(() => setContentVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // The overlay covers the Help bubble for exactly its mount lifetime, so it raises the
  // flag BuilderHelpCenter ORs into bubbleObscured (GFG-784, WCAG 2.2 SC 2.4.11).
  useEffect(() => {
    setFullscreenPreviewOpen(true);
    return () => setFullscreenPreviewOpen(false);
  }, [setFullscreenPreviewOpen]);

  // Exit instantly — no animation on the way out
  const handleExit = useCallback(() => onExit(), [onExit]);

  // ESC exits
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") handleExit();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [handleExit]);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const value = useMemo<FullscreenPreviewContextValue>(
    () => ({
      scrollRoot: scrollEl,
      exitControl: (
        <ExitControl
          condensed
          className="h-10"
          onExit={handleExit}
          selectedCountry={selectedCountry}
          onCountryChange={setSelectedCountry}
        />
      ),
      setExitInHeader,
    }),
    [scrollEl, handleExit, selectedCountry, setSelectedCountry],
  );

  return (
    <FullscreenPreviewContext.Provider value={value}>
      {/* Black backdrop — appears instantly on mount */}
      <div className="fixed inset-0 z-9999" style={{ background: "#000" }}>
        {/* Content layer — fades in from the black */}
        <div
          className="absolute inset-0 bg-wax flex flex-col overflow-y-auto"
          style={{
            opacity: contentVisible ? 1 : 0,
            transition: "opacity 1s ease-in-out",
          }}
        >
          {fillContent ? (
            <div className="absolute inset-0 overflow-hidden">{children}</div>
          ) : (
            // No padding here: it would offset `sticky top-0` descendants (e.g. the
            // Landing filter header) below the scrollport's top edge.
            <div ref={setScrollEl} className="flex-1 overflow-y-auto">
              <div className="px-5 py-5 flex justify-center items-start">{children}</div>
            </div>
          )}
        </div>

        {/* Exit Preview + Country picker — segmented pill, fixed top-right */}
        {!exitInHeader && (
          <ExitControl
            className="fixed top-4 right-5 z-10002"
            onExit={handleExit}
            selectedCountry={selectedCountry}
            onCountryChange={setSelectedCountry}
          />
        )}
      </div>
    </FullscreenPreviewContext.Provider>
  );
}
